import type { ColumnDef } from "@tanstack/react-table";

import type { BaseRowItem } from "../../components/data-table";
import type { AdminOrderTransaction } from "../../lib/api/adminTransactionsData";
import { formatAmount, truncateWord } from "../../utils/FormatPerson";

export type OrderTableRow = BaseRowItem & {
  raw: AdminOrderTransaction;
  displayDate: string;
  orderTypeLabel: string;
  statusLabel: string;
  summary: string;
  userIdDisplay: string;
  userIdFull: string;
};

function num(v: unknown): number | undefined {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function leg(fromTo: unknown): { asset?: string; amount?: number } | null {
  if (!fromTo || typeof fromTo !== "object") return null;
  const o = fromTo as Record<string, unknown>;
  return {
    asset: str(o.asset) || undefined,
    amount: num(o.amount),
  };
}

/** Primary timestamp for display: processedAt ?? createdAt */
export function getOrderDisplayDate(tx: AdminOrderTransaction): string {
  const processed = str(tx.processedAt);
  const created = str(tx.createdAt);
  const iso = processed || created;
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export function formatOrderSummary(tx: AdminOrderTransaction): string {
  const ot = (str(tx.orderType) || "").toLowerCase();
  const amtNgn = num(tx.amountNGN);

  if (ot === "sell") {
    const asset = str(tx.asset) || "—";
    const amount = num(tx.amount);
    const usdt = num(tx.amountUSDT);
    const parts = [`${asset} ${amount != null ? formatAmount(amount) : "—"}`];
    if (usdt != null) parts.push(`≈ ${formatAmount(usdt)} USDT`);
    if (amtNgn != null) parts.push(`₦${formatAmount(amtNgn)}`);
    return parts.join(" · ");
  }

  if (ot === "swap") {
    const f = leg(tx.from);
    const t = leg(tx.to);
    const fa = f?.asset ?? "—";
    const ta = t?.asset ?? "—";
    const fam = f?.amount != null ? formatAmount(f.amount) : "";
    const tam = t?.amount != null ? formatAmount(t.amount) : "";
    return `${fam} ${fa} → ${tam} ${ta}`.trim();
  }

  if (ot === "deposit") {
    const cur = str(tx.currency) || "—";
    const amount = num(tx.amount);
    const fees = num(tx.fees);
    const parts = [
      `${amount != null ? formatAmount(amount) : "—"} ${cur}`,
    ];
    if (amtNgn != null) parts.push(`₦${formatAmount(amtNgn)}`);
    if (fees != null && fees > 0) parts.push(`fee ${formatAmount(fees)}`);
    return parts.join(" · ");
  }

  if (ot === "withdrawal") {
    const cur = str(tx.currency) || "—";
    const amount = num(tx.amount);
    const net = num(tx.usdtAmount);
    const netw = str(tx.network);
    const addr = str(tx.toAddress);
    const deex = str(tx.toDeexUser);
    const parts = [`${amount != null ? formatAmount(amount) : "—"} ${cur}`];
    if (net != null) parts.push(`${formatAmount(net)} USDT`);
    if (netw) parts.push(netw);
    if (addr) parts.push(`${truncateWord(addr)}`);
    else if (deex) parts.push(`→ ${truncateWord(deex)}`);
    if (amtNgn != null) parts.push(`₦${formatAmount(amtNgn)}`);
    return parts.join(" · ");
  }

  const amount = num(tx.amount);
  const asset = str(tx.asset) || str(tx.currency);
  if (asset && amount != null) return `${formatAmount(amount)} ${asset}`;
  return "—";
}

export function statusBadgeClass(statusRaw: unknown): string {
  const u = str(statusRaw).toUpperCase();
  if (
    u === "COMPLETED" ||
    u === "SUCCESSFUL" ||
    u === "COMEPLETED"
  ) {
    return "bg-emerald-500/15 text-emerald-400 border-emerald-500/35";
  }
  if (u === "FAILED") {
    return "bg-red-500/15 text-red-300 border-red-500/35";
  }
  if (u === "PENDING" || u === "HOLD") {
    return "bg-amber-500/15 text-amber-300 border-amber-500/35";
  }
  return "bg-[#2C363E] text-[#AEBCC6] border-[#3A4852]";
}

export function orderTypeBadgeClass(orderTypeRaw: unknown): string {
  const ot = str(orderTypeRaw).toLowerCase();
  switch (ot) {
    case "sell":
      return "bg-orange-500/15 text-orange-300 border-orange-500/35";
    case "deposit":
      return "bg-emerald-500/15 text-emerald-300 border-emerald-500/35";
    case "swap":
      return "bg-violet-500/15 text-violet-300 border-violet-500/35";
    case "withdrawal":
      return "bg-sky-500/15 text-sky-300 border-sky-500/35";
    default:
      return "bg-[#2C363E] text-[#AEBCC6] border-[#3A4852]";
  }
}

export function buildOrderTableRows(
  transactions: AdminOrderTransaction[]
): OrderTableRow[] {
  return transactions.map((tx) => {
    const uid = str(tx.userId);
    let userIdDisplay = uid || "—";
    if (uid.length > 12) userIdDisplay = `${uid.slice(0, 6)}…${uid.slice(-4)}`;

    return {
      id: tx._id,
      raw: tx,
      displayDate: getOrderDisplayDate(tx),
      orderTypeLabel: str(tx.orderType) || "—",
      statusLabel: str(tx.status) || "—",
      summary: formatOrderSummary(tx),
      userIdDisplay,
      userIdFull: uid || "—",
    };
  });
}

export function createOrderTableColumns(
  onView: (row: OrderTableRow) => void
): ColumnDef<OrderTableRow, unknown>[] {
  return [
    {
      accessorKey: "displayDate",
      header: "Date",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="text-adminTable-muted">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "orderTypeLabel",
      header: "Type",
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${orderTypeBadgeClass(
            row.original.raw.orderType
          )}`}
        >
          {row.original.orderTypeLabel}
        </span>
      ),
    },
    {
      accessorKey: "statusLabel",
      header: "Status",
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${statusBadgeClass(
            row.original.raw.status
          )}`}
        >
          {row.original.statusLabel}
        </span>
      ),
    },
    {
      accessorKey: "summary",
      header: "Amount / detail",
      meta: { wrap: true, maxWidth: "280px" },
      cell: ({ getValue }) => (
        <span className="text-[13px] text-white">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "userIdDisplay",
      header: "User",
      meta: { hideBelow: "lg" as const },
      cell: ({ row }) => (
        <span className="text-adminTable-muted" title={row.original.userIdFull}>
          {row.original.userIdDisplay}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      meta: { align: "right" as const },
      cell: ({ row }) => (
        <button
          type="button"
          className="rounded-lg border border-[#3A4852] bg-[#1C2227] px-3 py-1.5 text-[12px] font-semibold text-secondary_button hover:border-secondary_button hover:bg-[#273037]"
          onClick={() => onView(row.original)}
        >
          View
        </button>
      ),
    },
  ];
}
